/**
 * MCSR (Monthly Cost Status Report) shared frontend types + formatting.
 * Mirrors backend/src/models/mcsr.types.ts — keep in sync.
 */

export interface McsrMeasures {
  originalBudget: number;
  approvedInternalChanges: number;
  approvedClientChanges: number;
  currentBudget: number;
  expendedThisPeriod: number;
  expendedUptoDate: number;
  balanceToExpend: number;
  committedToDate: number;
  estimateToCompletion: number;
  currentFinalForecast: number;
  previousForecast: number;
  overUnderRun: number;
  changeSinceLastReport: number;
  pendingClientChanges: number;
  storeValue: number;
}

export const MEASURE_KEYS: (keyof McsrMeasures)[] = [
  'originalBudget',
  'approvedInternalChanges',
  'approvedClientChanges',
  'currentBudget',
  'expendedThisPeriod',
  'expendedUptoDate',
  'balanceToExpend',
  'committedToDate',
  'estimateToCompletion',
  'currentFinalForecast',
  'previousForecast',
  'overUnderRun',
  'changeSinceLastReport',
  'pendingClientChanges',
  'storeValue',
];

/** Exact report captions — numbering, spelling and typos kept verbatim. */
export const MCSR_COLUMN_CAPTIONS: string[] = [
  '1-Original Budget (PCD Input)',
  '2-Approved Internal Changes (PCD Input)',
  '3-Approved Client Changes (PCD Input)',
  '4-Current Budget (4 = (1 + 2 + 3))',
  '5-Expended This Period(Procurment + Subcontractor )',
  '6-Expended Upto Date(FAD module collection ',
  '7-Balance to Expend (7 = 8-6)',
  '8-Committed to Date (PO + SC)',
  '9-Estimate to Complete (9 = 10-8)',
  '10-Current Final Forecast (PCD Input)',
  '11-Previous Forecast (PCD Input)',
  '12-Over/UnderRun (12 = 10 - 4)',
  '13-Change Since last (13 = 10 - 11)',
  '14-Pending client changes (PCD Input)',
  '15-Store Value (MRV - MIV)',
];

export type McsrRowKind =
  | 'detail'
  | 'group-total'
  | 'classification-subtotal'
  | 'classification-total'
  | 'grand-total';

export interface McsrReportRow extends McsrMeasures {
  kind: McsrRowKind;
  classificationCode: string | null;
  classificationName: string | null;
  groupCode: string | null;
  costGroup: string | null;
  costCode: string | null;
  projectCostType: string | null;
  label: string | null;
  identityWarnings?: string[];
}

export interface McsrFinancialAnalysisView {
  workDone: { periodPlanned: number; periodActual: number; toDateActual: number; toDatePlanned: number };
  provisionalWorkDone: { periodActual: number };
  sundryIncome: { periodActual: number; toDateActual: number };
  totalIncome: { periodActual: number; toDateActual: number };
  totalCost: { periodActual: number; toDateActual: number };
  overallProfit: { periodActual: number; toDateActual: number };
}

export interface McsrHeader {
  companyName: string;
  title: string;
  project: string;
  projectName: string;
  customerCode: string | null;
  customerName: string | null;
  monthYear: string;
  fromDate: string;
  toDate: string;
}

export interface McsrReportPayload {
  header: McsrHeader;
  summary: McsrReportRow[];
  detail: McsrReportRow[];
  financialAnalysis: McsrFinancialAnalysisView;
  validation: {
    identityWarnings: string[];
    sectionReconciliationWarnings: string[];
  };
}

export interface McsrProjectOption {
  project: string;
  projectName: string;
  customerCode: string | null;
  customerName: string | null;
}

export interface McsrProjectListRow extends McsrMeasures {
  project: string;
  projectName: string;
  customerCode: string | null;
  customerName: string | null;
  monthYear: string;
}

export interface McsrTrendPoint extends McsrMeasures {
  month: string;
  monthYear: string;
}

export interface McsrDrillTransaction {
  source: string;
  docEntry: string;
  docNum: string | null;
  docDate: string | null;
  amount: number;
  memo: string | null;
}

// ---- Sync Monitor (start + watch the MCSR data import from SAP B1) ----

export type McsrSourceKind = 'addon' | 'document' | 'master';
export type McsrSyncState = 'Running' | 'Queued' | 'Idle' | 'Paused' | 'Missing';

export interface McsrSyncScheduleStatus {
  scheduleId: number | null;
  endpointKey: string;
  label: string;
  kind: McsrSourceKind;
  state: McsrSyncState;
  cron: string | null;
  nextRunAt: string | null;
  lastRun: {
    runId: number;
    startedAt: string;
    finishedAt: string | null;
    status: string;
    rows: number | null;
    error: string | null;
  } | null;
  lastSuccessAt: string | null;
  backfill: { startDate: string | null; cursor: string | null; complete: boolean } | null;
  rowsInApp: number;
  lastImportedAt: string | null;
  latestDataDate: string | null;
}

export interface McsrSyncPeriod {
  month: string;
  snapshots: number;
  projects: number;
  costLines: number;
  lastImportedAt: string | null;
}

export interface McsrSyncStatusPayload {
  serverTime: string;
  schedules: McsrSyncScheduleStatus[];
  periods: McsrSyncPeriod[];
}

export interface McsrSyncRunResult {
  queued: number[];
  resumed: number[];
  skipped: Array<{ scheduleId: number; reason: string }>;
}

/** #,##0.000 (KWD 3-decimals), negative sign preserved, -0 normalized. */
export function fmtMcsr(value: number): string {
  const v = Object.is(value, -0) ? 0 : value || 0;
  return v.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** 0.00% style: "9.17%", bare "0%" for zero. */
export function fmtMcsrPercent(fraction: number): string {
  if (!fraction || Math.abs(fraction) < 1e-9) return '0%';
  return `${(fraction * 100).toFixed(2)}%`;
}

/** Compact KPI format (e.g. 2.11M) for dashboard cards. */
export function fmtMcsrCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return fmtMcsr(value);
}

// ---- Bulk print and export (backend services/mcsr/mcsrBulk.util.ts, mcsrRun.service.ts) ----

/** A project the bulk dialog lists: its parent (SAP Parent Project; null for a parent), division and department. */
export interface McsrBulkProject {
  project: string;
  projectName: string;
  customerCode: string | null;
  customerName: string | null;
  parent: string | null;
  division: string | null;
  divisionName: string | null;
  department: string | null;
  departmentName: string | null;
  /** The project has an MCSR in the month asked for. */
  hasReport: boolean;
}

export interface McsrBulkCatalog {
  projects: McsrBulkProject[];
  /** False when SAP's project list could not be read (no parent, division or department then). */
  sapProjects: boolean;
}

export interface McsrBulkOutputs {
  pdf: boolean;
  zip: boolean;
  xlsx: boolean;
}

export type McsrBulkRunStatus = 'Queued' | 'Running' | 'Done' | 'Failed' | 'Cancelled';

export interface McsrBulkRun {
  runId: number;
  companyId: number;
  month: string;
  monthLabel: string;
  parentsOnly: boolean;
  outputs: McsrBulkOutputs;
  projects: Array<{ project: string; projectName: string; parent: string | null }>;
  total: number;
  done: number;
  status: McsrBulkRunStatus;
  message: string | null;
  errors: Array<{ project: string; message: string }>;
  hasPdf: boolean;
  hasZip: boolean;
  hasXlsx: boolean;
  pages: number | null;
  createdBy: number | null;
  createdByName: string | null;
  createdDate: string;
  finishedDate: string | null;
}
